// src/config/firebase.ts
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// 設定値の検証（開発環境）
if (import.meta.env.DEV) {
  console.log('🔧 管理システム Firebase設定開始...');

  const requiredEnvVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
  ];

  const missingVars = requiredEnvVars.filter((varName) => !import.meta.env[varName]);

  if (missingVars.length > 0) {
    console.error('🔥 Firebase設定エラー: 以下の環境変数が設定されていません:');
    missingVars.forEach((varName) => console.error(`  ❌ ${varName}`));
    console.error('💡 .env.localファイルを確認してください');
  } else {
    console.log('✅ Firebase環境変数が正常に読み込まれました');
    console.log(`📱 プロジェクトID: ${firebaseConfig.projectId}`);
    console.log(`🌐 エミュレーター使用: ${import.meta.env.VITE_USE_FIREBASE_EMULATOR}`);
  }
}

// Firebase初期化
export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// エミュレーター使用時はリージョン指定を削除
export const functions =
  import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'
    ? getFunctions(app) // エミュレーター用（リージョン指定なし）
    : getFunctions(app, 'asia-northeast2'); // 本番用（大阪リージョン）

// 開発環境でのエミュレータ接続
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  try {
    console.log('🔧 Firebase Emulator (管理システム)に接続中...');

    // 既に接続済みかチェック
    if (!(auth as any)._delegate._isProactiveRefreshEnabled) {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      console.log('✅ Auth Emulator: http://localhost:9099');
    }

    if (!(db as any)._delegate._terminated) {
      connectFirestoreEmulator(db, 'localhost', 8080);
      console.log('✅ Firestore Emulator: http://localhost:8080');
    }

    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('✅ Functions Emulator: http://localhost:5001');

    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('✅ Storage Emulator: http://localhost:9199');

    console.log('🎉 Firebase Emulator接続完了（管理システム）');
  } catch (error: any) {
    if (error.message?.includes('already exists')) {
      console.log('✅ Firebase Emulator は既に接続済みです');
    } else {
      console.warn('⚠️ Firebase Emulatorへの接続に失敗:', error.message);
      console.log('💡 Firebase Emulator が起動していることを確認してください');
      console.log('   起動コマンド: cd insurance-simulator && start-emulator.bat');
    }
  }
} else {
  console.log('🌐 Firebase本番環境に接続しました（管理システム）');
  console.log(`📱 Project: ${firebaseConfig.projectId}`);
  console.log(`🌏 Region: asia-northeast2`);
}
